import React, { useMemo, useEffect, useRef, useState } from 'react';
import { LuArrowUp, LuChevronDown, LuChevronLeft, LuChevronRight, LuPencil, LuSettings, LuX } from 'react-icons/lu';
import { StyledHeader } from './Header.styles';
import linkedinImg from '../../../assets/icon-linkedin-scraper.svg';
import messageImg from '../../../assets/icon-message-enrichment.svg';
import emailFinderImg from '../../../assets/icon-email-enrichment.svg';
import emailSendImg from '../../../assets/icon-email-sending.svg';
import coinsImg from '../../../assets/images/icon-coins.svg';
import iconPlay from '../../../assets/icon-play.png';
import iconStop from '../../../assets/icon-stop.png';
import ProfilePic from '../../../assets/uploadIcon.svg';
import coinImg from '../../../assets/icon-coin.svg';
import NavData from '../../../nav.json';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  calculatePercentage,
  formatStatus,
  getGreetingMessage,
  handleCreditsNames,
  formatLastActivity,
  formatDate,
  formatTime,
} from '../../../helpers/common';
import { AuthContext } from '../../../Context/authContext';
import { CONNECT_LINKEDIN_ROUTE } from '../../../helpers/linkedinStep';
import { SubListLeadsContext } from '../../../Context/subListLeadsContext';
import { JobLeadsContext } from '../../../Context/jobLeadsContext';
import { useContextHook } from 'use-context-hook';
import Button from '../../molecules/Button';
import linkedinService from '../../../services/linkedinService';
import jobService from '../../../services/jobService';
import Toast from '../../molecules/Toast';
import campaignService from '../../../services/campaignService';
import { CampaignAnalyticsContext } from '../../../Context/getCampaignName';
import { PendingApprovalsContext } from '../../../Context/pendingApprovalsContext';
import useClickOutside from '../../../hooks/useClickOutside';

const Header = () => {
  const [profileDrop, setProfileDrop] = useState(false);
  const [creditActive, setCreditActive] = useState(false);
  const ProfileDropRef = useRef(null);
  const CreditRef = useRef(null);
  const location = useLocation();
  const campaignId = location?.state?.campaign_id;
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editingCampaignName, setEditingCampaignName] = useState('');
  const [campaignStatus, setCampaignStatus] = useState(location?.state?.status || 'running');
  const inputRef = useRef(null);
  const [quickActive, setQuickActive] = useState(false);
  const quickRef = useRef(null);
  const { id } = useParams();
  const [emailCreationUnderDomainData, setEmailCreationUnderDomainData] = useState(null);
  const [isEditingList, setIsEditingList] = useState(false);
  const [editingListName, setEditingListName] = useState('');
  const [isEditingJob, setIsEditingJob] = useState(false);
  const [editingJobName, setEditingJobName] = useState('');
  const [lastActivity, setLastActivity] = useState(null);
  const crownEnabled = String(process.env.REACT_APP_CROW_ENABLED).toLowerCase() === 'true';
  const { user, hasPermission, permissions, getPermissions, refetch, allowedPages, fetchUser } = useContextHook(
    AuthContext,
    v => ({
      user: v.user,
      refetch: v.refetch,
      hasPermission: v.hasPermission,
      permissions: v.permissions,
      allowedPages: v.allowedPages,
      fetchUser: v.fetchUser,
      getPermissions: v.getPermissions,
    }),
  );

  const isPendingApprovalsPage = location?.pathname === '/pending-approvals';
  const { pendingApprovalsTotalCount } = useContextHook(PendingApprovalsContext, v => ({
    pendingApprovalsTotalCount: v.totalCount,
  }));

  const canRenderCrow = crownEnabled && user?.data?.featureAccess?.agentAccess;
  // Get sublist name from context
  const { subListName } = useContextHook(SubListLeadsContext, v => ({
    subListName: v.subListName,
  }));

  const { jobName } = useContextHook(JobLeadsContext, v => ({
    jobName: v.jobName,
  }));
  const { campaignName: contextCampaignName } = useContextHook(CampaignAnalyticsContext, v => ({
    campaignName: v.campaignName,
  }));

  useEffect(() => {
    if (contextCampaignName && !editingCampaignName) {
      setEditingCampaignName(contextCampaignName);
    }
  }, [contextCampaignName]);

  useEffect(() => {
    if (subListName && !editingListName) {
      setEditingListName(subListName);
    }
  }, [subListName]);

  useEffect(() => {
    if (jobName && !editingJobName) {
      setEditingJobName(jobName);
    }
  }, [jobName]);

  useEffect(() => {
    if (location.pathname.startsWith('/lead-preview') && location.state?.lastActivity) {
      setLastActivity(location.state.lastActivity);
    }
  }, [location.pathname, location.state?.lastActivity]);

  // Listen for Quick Actions opening from other components to close Credits dropdown
  useEffect(() => {
    const handleQuickActionsOpen = () => {
      setCreditActive(false);
    };
    window.addEventListener('quick-actions-opened', handleQuickActionsOpen);
    return () => {
      window.removeEventListener('quick-actions-opened', handleQuickActionsOpen);
    };
  }, []);

  useEffect(() => {
    // keep local status in sync with router state updates
    if (location?.state?.status) {
      setCampaignStatus(location.state.status);
    }
  }, [location?.state?.status]);

  // Track emailCreationUnderDomain from sessionStorage
  useEffect(() => {
    const checkEmailCreationUnderDomain = () => {
      const stored = sessionStorage.getItem('emailCreationUnderDomain');
      setEmailCreationUnderDomainData(stored ? JSON.parse(stored) : null);
    };

    // Check on mount and when pathname changes
    checkEmailCreationUnderDomain();

    // Listen for storage events (when sessionStorage changes in other tabs/components)
    const handleStorageChange = e => {
      if (e.key === 'emailCreationUnderDomain') {
        checkEmailCreationUnderDomain();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically for changes (since storage event doesn't fire in same tab)
    const interval = setInterval(checkEmailCreationUnderDomain, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [location.pathname]);

  const handleTabClick = tab => {
    sessionStorage.setItem('emailAccountActiveTab', tab);
    navigate(location.pathname, { state: { activeTab: tab } });
  };

  const handleQuickAction = async () => {
    try {
      const payload = {
        list_id: Number(id),
      };
      const res = await linkedinService.emailEnrichment(payload);

      if (res.success) {
        Toast({ type: 'success', message: 'Email Enrichment Initiated' });
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message });
    }
  };

  const pageName = NavData?.find(i => i?.route === location?.pathname);

  const handleClickOutsideCredit = event => {
    if (CreditRef.current && !CreditRef.current.contains(event.target)) {
      setCreditActive(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setIsEditing(false);
        setIsEditingList(false);
      }
    };

    document.addEventListener('click', handleClickOutsideCredit);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutsideCredit);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handlePauseCampaign = async () => {
    try {
      const res = await campaignService.pauseCampaign(campaignId);
      if (res.success) {
        setCampaignStatus('paused');
        Toast({ type: 'success', message: res.message });
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message });
    }
  };

  const handleResumeCampaign = async () => {
    try {
      const res = await campaignService.resumeCampaign(campaignId);
      if (res.success) {
        setCampaignStatus('running');
        Toast({ type: 'success', message: res.message });
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message });
    }
  };

  const handleRenameCampaign = async e => {
    try {
      // setLoading(true);
      const payload = {
        campaignId: campaignId,
        name: editingCampaignName,
      };
      const res = await campaignService.renameCampaign(payload);
      if (res?.success) {
        Toast({ type: 'success', message: res?.message });
        refetch();
      }
    } catch (error) {
      Toast({ type: 'error', message: error?.message });
    } finally {
      // setLoading(false);
    }
  };

  const handleRenameList = async () => {
    try {
      if (!editingListName?.trim()) {
        Toast({ type: 'error', message: 'Please enter a list name' });
        return;
      }
      const res = await linkedinService.updateListName({ id: id, name: editingListName });
      if (res?.success) {
        Toast({ type: 'success', message: 'List renamed successfully' });
        refetch();
      }
    } catch (error) {
      Toast({ type: 'error', message: error?.message || 'Failed to rename list' });
    }
  };

  const handleRenameJob = async () => {
    try {
      if (!editingJobName?.trim()) {
        Toast({ type: 'error', message: 'Please enter a job name' });
        return;
      }
      const res = await jobService.updateJobName({ id: id, name: editingJobName });
      if (res?.success) {
        Toast({ type: 'success', message: 'Job renamed successfully' });
        setIsEditingJob(false);
        refetch();
      }
    } catch (error) {
      Toast({ type: 'error', message: error?.message || 'Failed to rename job' });
    }
  };

  const dashboardPages = ['/get-started', '/dashboard'];
  const leadsModulePages = ['/leads-list', '/leads-hub'];
  const emailPages = ['/email-accounts', '/domains'];

  const storedEmailTab = sessionStorage.getItem('emailAccountActiveTab');
  const isOldUser = new Date(user?.data?.created_at) < new Date('2026-03-09');

  const defaultEmailTab = isOldUser ? 'domain' : 'single';

  let activeTab = location?.state?.activeTab || storedEmailTab || defaultEmailTab;

  if (!isOldUser && activeTab === 'domain') {
    activeTab = 'single';
  }

  const openCrowCopilot = () => {
    if (window?.crow) {
      window.crow('open');
    }
  };

  /* The offer pages (/offers, /offers/:id, /create-offer) render their own back
     link, heading and unsaved-changes prompt, so Layout hides this Header there
     entirely — see components/atoms/Offers. */

  return (
    <StyledHeader>
      <div className="header-main-row">
        <div className="heading-box">
          {location.pathname === '/create-campaign' && !location?.state?.isEdit ? (
            <strong className="launchpad">Launchpad 🚀</strong>
          ) : location.pathname === '/create-campaign' && location?.state?.isEdit ? (
            <>
              <div className="edit-name-header">
                <div className="campaign-name-holder">
                  <button className="btn-back" onClick={() => navigate(-1)}>
                    <LuChevronLeft />
                  </button>
                  <div className="text-wrap" ref={inputRef}>
                    {isEditing ? (
                      <input
                        className="campaign-name-input"
                        type="text"
                        value={editingCampaignName}
                        autoFocus
                        onChange={e => setEditingCampaignName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRenameCampaign();
                            setIsEditing(false);
                          }
                        }}
                      />
                    ) : (
                      <>
                        <span className="campaign-name">{contextCampaignName || editingCampaignName}</span>
                        {campaignStatus !== 'completed' && (
                          <LuPencil
                            className="edit"
                            onClick={e => {
                              e.stopPropagation();
                              setEditingCampaignName(contextCampaignName || '');
                              setIsEditing(true);
                              setCreditActive(false);
                              window.dispatchEvent(new CustomEvent('close-quick-actions'));
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                  <span className="tag edit-tag">Campaign</span>
                </div>
                {(campaignStatus === 'running' || campaignStatus === 'paused') && (
                  <div className="btn-wrap">
                    {campaignStatus === 'paused' ? (
                      <button className="btn-resume" onClick={() => handleResumeCampaign()}>
                        <img src={iconPlay} alt="play" /> Running Campaign
                      </button>
                    ) : (
                      <button className="btn-stop" onClick={() => handlePauseCampaign()}>
                        <img src={iconStop} alt="stop" /> Pause Campaign
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : dashboardPages.includes(location.pathname) ? (
            <>
              {/* <button
              className={`btn ${location.pathname === '/get-started' ? 'active' : ''}`}
              onClick={() => navigate('/get-started')}>
              Get Started
            </button> */}
              <button
                className={`btn ${location.pathname === '/get-started' ? 'active' : ''}`}
                onClick={() => navigate('/get-started')}>
                <span id="get-started-btn">Get Started</span>
              </button>
              {/* Parked — the Dashboard tab. /dashboard is parked in nav.json, so
                  this button would bounce to AI Home. Get Started keeps working.

              <button
                className={`btn ${location.pathname === '/dashboard' ? 'active' : ''}`}
                onClick={() => navigate('/dashboard')}>
                <span id="dashboard-btn">
                  Dashboard
                </span>
              </button>
              */}
            </>
          ) : leadsModulePages.includes(location.pathname) ? (
            <>
              <button
                className={`btn ${location.pathname === '/leads-list' ? 'active' : ''}`}
                onClick={() => navigate('/leads-list')}>
                <span id="leads-list-btn">Leads List</span>
              </button>

              {process.env.REACT_APP_ENABLE_ADVANCE_SEARCH === 'true' && (
                <button
                  className={`btn ${location.pathname === '/leads-hub' ? 'active' : ''}`}
                  onClick={() => navigate('/leads-hub')}>
                  <span id="leads-hub-btn">Leads Hub</span>
                </button>
              )}
            </>
          ) : emailPages.includes(location.pathname) && emailCreationUnderDomainData ? (
            <>
              <div className="edit-name-header">
                <div className="campaign-name-holder">
                  <button
                    className="btn-back"
                    onClick={() => {
                      sessionStorage.removeItem('emailCreationUnderDomain');
                      navigate(location.pathname, { state: { activeTab: 'domain' }, replace: true });
                    }}>
                    <LuChevronLeft />
                  </button>
                  <strong className="domain-name">{emailCreationUnderDomainData.domainName || 'Domain Emails'}</strong>
                  <span className="tag domain-tag">Domain</span>
                </div>
              </div>
            </>
          ) : emailPages.includes(location.pathname) ? (
            <div className="email-account-btns">
              {new Date(user?.data?.created_at) < new Date('2026-03-09') && (
                <button
                  className={`btn ${activeTab === 'domain' ? 'active' : ''}`}
                  onClick={() => handleTabClick('domain')}>
                  <span id="domains-btn">Domains</span>
                </button>
              )}
              <button
                className={`btn ${activeTab === 'single' ? 'active' : ''}`}
                onClick={() => handleTabClick('single')}>
                <span id="email-accounts-btn">Email Accounts</span>
              </button>
            </div>
          ) : location.pathname.startsWith('/single-list-lead') ? (
            <>
              <div className="edit-name-header">
                <div className="campaign-name-holder">
                  <button className="btn-back" onClick={() => navigate(-1)}>
                    <LuChevronLeft />
                  </button>
                  <div className="text-wrap" ref={inputRef}>
                    {isEditingList ? (
                      <input
                        className="campaign-name-input"
                        type="text"
                        value={editingListName}
                        autoFocus
                        onChange={e => setEditingListName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRenameList();
                            setIsEditingList(false);
                          }
                        }}
                      />
                    ) : (
                      <>
                        <span className="campaign-name">{subListName || editingListName}</span>
                        <LuPencil
                          className="edit"
                          onClick={e => {
                            e.stopPropagation();
                            setEditingListName(subListName || '');
                            setIsEditingList(true);
                            setCreditActive(false);
                            window.dispatchEvent(new CustomEvent('close-quick-actions'));
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : location.pathname.startsWith('/single-job-lead') ? (
            <>
              <div className="edit-name-header">
                <div className="campaign-name-holder">
                  <button className="btn-back" onClick={() => navigate(-1)}>
                    <LuChevronLeft />
                  </button>
                  <div className="text-wrap" ref={inputRef}>
                    {isEditingJob ? (
                      <input
                        className="campaign-name-input"
                        type="text"
                        value={editingJobName}
                        autoFocus
                        onChange={e => setEditingJobName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRenameJob();
                            setIsEditingJob(false);
                          }
                        }}
                      />
                    ) : (
                      <>
                        <span className="campaign-name">{jobName || editingJobName}</span>
                        <LuPencil
                          className="edit"
                          onClick={e => {
                            e.stopPropagation();
                            setEditingJobName(jobName || '');
                            setIsEditingJob(true);
                            setCreditActive(false);
                            window.dispatchEvent(new CustomEvent('close-quick-actions'));
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : location.pathname.startsWith('/lead-preview') ? (
            <>
              <div className="edit-name-header">
                <div className="campaign-name-holder">
                  <button className="btn-back" onClick={() => navigate(-1)}>
                    <LuChevronLeft />
                  </button>
                  <strong className="lead-name">Lead Preview</strong>
                  <span className="tag">Lead</span>
                </div>
                <div className="info-active">
                  <span className="text"> Last Activity</span> |{' '}
                  <span className="date"> {`${formatDate(lastActivity)} at ${formatTime(lastActivity)}`}</span>
                </div>
              </div>
            </>
          ) : location.pathname === '/campaign-launched' ? (
            <h1 className="page-title">Campaign Launched 🚀</h1>
          ) : location.pathname === '/create-task' ? (
            <h1 className="page-title">Create task</h1>
          ) : location.pathname === '/pending-approvals' ? (
            <h1 className="page-title">
              Pending Approvals&nbsp;
              <span className="pending-approvals-count">({pendingApprovalsTotalCount ?? 0})</span>
            </h1>
          ) : (
            <h1 className="page-title">{pageName?.name}</h1>
          )}
        </div>

        <div className="header-right-col">
          {canRenderCrow && (
            <button type="button" className="copilot-btn" onClick={openCrowCopilot} variant="gradient">
              Copilot
            </button>
          )}

          {/* complete onboarding button if user didn't complete it and has no linkedin profile connected */}
          {user?.data?.status !== 'onboarding_completed' && !user?.data?.linkedinProfileExists && (
            <button
              type="button"
              className="complete-onboarding-btn"
              variant="gradient"
              onClick={() => navigate(CONNECT_LINKEDIN_ROUTE)}>
              Complete Onboarding
            </button>
          )}

          {/* Notifications and the sidebar hamburger now live in the TopBar. */}
        </div>
      </div>
    </StyledHeader>
  );
};

export default Header;
